import { useEffect, useState } from 'react';
import { ModularFormFieldProps } from 'react-modular-forms/dist/declarations/types';
import { UserFile, UserFileType } from '@he-novation/config/types/user-file.types';
import { FormField } from '@he-novation/design-system/components/form/FormField/FormField';
import {
    asyncWorkspaceUserFileCreate,
    asyncWorkspaceUserFileDelete
} from '@he-novation/front-shared/async/workspaceUserFile.async';
import { useAtomValue } from 'jotai';

import { workspaceNameAtom } from '$atoms/workspace-atoms';

export type UserFilePickerProps = ModularFormFieldProps & {
    type: UserFileType;
    value?: UserFile | null;
    values: UserFile[];
    onAdd?: (item: UserFile) => void;
    onChange?: (item: UserFile | null) => void;
    onRemove?: (uid: string) => void;
};

export function UserFilePicker({
    type,
    value,
    values,
    onAdd,
    onChange,
    onRemove,
    ...props
}: UserFilePickerProps) {
    const workspaceName = useAtomValue(workspaceNameAtom);

    const [selectedUserFile, setSelectedUserFile] = useState<UserFile | null>(value || null);
    const [availableUserFiles, setAvailableUserFiles] = useState<UserFile[]>(values);

    useEffect(() => {
        setAvailableUserFiles(values);
    }, [values]);

    useEffect(() => {
        if (!value) return;
        setSelectedUserFile(value);
    }, [value]);

    return (
        <FormField
            {...props}
            type={'graphic-picker'}
            value={selectedUserFile}
            values={values}
            onAdd={(file: File) => {
                asyncWorkspaceUserFileCreate(workspaceName, type, file).then((userFile) => {
                    setAvailableUserFiles((prev) => [...prev, userFile]);
                    setSelectedUserFile(userFile);
                    onAdd?.(userFile);
                });
            }}
            onChange={(item: UserFile | null) => {
                const userFile = item ? availableUserFiles.find((f) => f.uid == item.uid) : null;

                if (userFile === undefined) return;
                setSelectedUserFile(userFile);
                onChange?.(userFile);
            }}
            onRemove={({ uid }: UserFile) => {
                asyncWorkspaceUserFileDelete(workspaceName, uid).then(() => {
                    setAvailableUserFiles((prev) => prev.filter((f) => f.uid !== uid));
                    onRemove?.(uid);
                });
            }}
        />
    );
}
